import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser } from 'lucide-react';

export interface SignaturePadHandle {
  getDataUrl: () => string | null;
  isEmpty: () => boolean;
  clear: () => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, { label?: string }>(({ label }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const drawing = useRef(false);
  const sized = useRef(false);
  const isEmptyRef = useRef(true);

  // Size the canvas backing store to its rendered box. The pad used to do this
  // once on mount — but when the "Review & sign" step mounts before layout
  // settles the box can report zero width, leaving the canvas unusable (looks
  // greyed-out, ignores strokes). A ResizeObserver re-sizes whenever the box
  // gains/changes width and restores any existing drawing so nothing is lost.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const applySize = (): void => {
      const ctx = c.getContext('2d');
      if (!ctx) return;
      const w = c.clientWidth, h = c.clientHeight;
      if (w === 0 || h === 0) return; // not laid out yet — wait for the observer
      const dpr = window.devicePixelRatio || 1;
      const targetW = Math.round(w * dpr), targetH = Math.round(h * dpr);
      if (c.width === targetW && c.height === targetH) return; // already correct
      // Preserve current drawing across the resize.
      const prev = sized.current && !isEmptyRef.current
        ? (() => { try { return c.toDataURL('image/png'); } catch { return null; } })()
        : null;
      c.width = targetW;
      c.height = targetH;
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.strokeStyle = '#061935';
      if (prev) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, w, h);
        img.src = prev;
      }
      sized.current = true;
    };
    applySize();
    const ro = new ResizeObserver(() => applySize());
    ro.observe(c);
    return () => ro.disconnect();
  }, []);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d'); if (!ctx) return;
    drawing.current = true;
    const [x, y] = pos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* non-fatal on synthetic events */ }
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d'); if (!ctx) return;
    const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke();
    if (isEmpty) { setIsEmpty(false); isEmptyRef.current = false; }
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height); setIsEmpty(true); isEmptyRef.current = true;
  };

  useImperativeHandle(ref, () => ({
    getDataUrl: () => canvasRef.current?.toDataURL('image/png') ?? null,
    isEmpty: () => isEmpty,
    clear,
  }));

  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-semibold text-raptor-navy">{label}</div>}
      <div className="border-2 border-dashed border-raptor-blue/40 rounded-md bg-white">
        <canvas
          ref={canvasRef}
          className="w-full h-40 touch-none"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-raptor-navy/60 font-body">Draw with finger or mouse. Signature is captured with timestamp + IP + geofence for the audit certificate.</span>
        <Button type="button" size="sm" variant="outline" onClick={clear} disabled={isEmpty}><Eraser size={12} /> Clear</Button>
      </div>
    </div>
  );
});
SignaturePad.displayName = 'SignaturePad';
